import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Calendar, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage, t, API } from './Common';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      navigate('/');
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.calendly_url) {
        setCalendlyUrl(response.data.calendly_url);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const checkPaymentStatus = async (attempt = 0) => {
    const maxAttempts = 5;
    
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      if (attempt < maxAttempts) {
        setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
      } else {
        setPaymentStatus('timeout');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempt < maxAttempts) {
        setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
      } else {
        setPaymentStatus('error');
      }
    }
  };

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
          <p className="text-xl text-muted-foreground">{t('processing', language)}</p>
        </div>
      </div>
    );
  }

  if (paymentStatus !== 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-panel rounded-sm p-8 md:p-12 max-w-2xl text-center">
          <p className="text-xl text-secondary mb-4">Payment verification failed or expired.</p>
          <Button
            onClick={() => navigate('/')}
            className="rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm"
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="scan-line" />
      <LanguageSwitcher />
      <div className="max-w-4xl mx-auto py-12">
        <div className="glass-panel rounded-sm p-8 md:p-12 text-center border-2 border-primary neon-glow mb-8">
          <CheckCircle className="w-20 h-20 text-primary mx-auto mb-6" data-testid="payment-success-icon" />
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">{t('paymentSuccess', language)}</h1>
          <p className="text-lg text-muted-foreground">{t('thankYou', language)}</p>
        </div>

        {calendlyUrl && (
          <div className="glass-panel rounded-sm p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 flex items-center justify-center gap-3">
              <Calendar className="w-8 h-8 text-premium" />
              {t('scheduleAppointment', language)}
            </h2>
            <p className="text-muted-foreground mb-6 text-center">{t('calendlyDesc', language)}</p>
            
            <div className="aspect-video bg-black/20 rounded-sm border border-white/10 overflow-hidden">
              <iframe
                src={calendlyUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                data-testid="calendly-widget"
                title="Schedule Appointment"
              />
            </div>
          </div>
        )}

        {!calendlyUrl && (
          <div className="glass-panel rounded-sm p-8 text-center">
            <p className="text-muted-foreground mb-4">
              Please contact Smart Info to schedule your appointment.
            </p>
            <Button
              onClick={() => navigate('/')}
              data-testid="return-home-button"
              className="rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm"
            >
              Return Home
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
